<script setup lang="ts">
const { assistantSettings } = useAssistantSettingsStore();

// Props with defaults
const {
  enabled = true,
  repeat = true,
  startAfter = 3,
  repeatAfter = 10,
} = defineProps<{
  enabled?: boolean;
  startAfter?: number;
  repeat?: boolean;
  repeatAfter?: number;
}>();

// Reactive state
const running = ref(false);

// Timer reference for cleanup
let repeatTimer: number | null = null;
let startDelayTimer: number | null = null;

// Animation logic
const startAnimation = () => {
  running.value = false;

  // Slight delay before activating the animation
  startDelayTimer = window.setTimeout(() => {
    running.value = true;

    // Set up repeat timer if enabled
    if (repeat) {
      scheduleAnimation();
    }
  }, 10);
};

const scheduleAnimation = () => {
  // prevent overlapping timers
  clearTimers();
  // Set timer to Re-trigger animation
  repeatTimer = window.setTimeout(() => {
    // Re-trigger animation
    startAnimation();
  }, repeatAfter * 1000);
};

const clearTimers = () => {
  if (repeatTimer) {
    clearTimeout(repeatTimer);
    repeatTimer = null;
  }
  if (startDelayTimer) {
    clearTimeout(startDelayTimer);
    startDelayTimer = null;
  }
};

// Initial trigger
if (enabled && assistantSettings.config.pulsate_widget) {
  window.setTimeout(scheduleAnimation, startAfter * 1000);
}

// Class binding
const classObject = computed(() => ({
  "is-pulsating": running.value,
}));

// Cleanup when component is destroyed
onBeforeUnmount(() => {
  clearTimers();
});
</script>

<template>
  <div v-if="enabled" class="widget-button-pulse" :class="classObject">
    <div class="pulse"></div>
    <div class="jump">
      <slot></slot>
    </div>
  </div>
  <div v-else>
    <slot></slot>
  </div>
</template>

<style lang="scss" scoped>
.widget-button-pulse {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 100%;
  height: 100%;

  .pulse {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    aspect-ratio: 1;

    &:after,
    &:before {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border-radius: 50%;
    }
  }

  &.is-pulsating {
    .jump {
      animation: pulseJump 1s ease-out 1;
    }

    .pulse {
      &:after {
        animation: pulse 1s 0.65s ease-out 1;
      }

      &:before {
        animation: pulse 1s 0.5s ease-out 1;
      }
    }
  }
}

@keyframes pulseJump {
  0% {
    transform: scale(1);
  }
  40% {
    transform: scale(1.08);
  }
  50% {
    transform: scale(0.98);
  }
  55% {
    transform: scale(1.02);
  }
  65% {
    transform: scale(0.98);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes pulse {
  0% {
    box-shadow:
      0 0 2px rgba(0, 0, 0, 0.15),
      inset 0 0 1px rgba(0, 0, 0, 0.15);
    transform: scale(1);
  }
  95% {
    box-shadow:
      0 0 50px rgba(0, 0, 0, 0),
      inset 0 0 30px rgba(0, 0, 0, 0);
  }
  100% {
    transform: scale(2.25);
  }
}
</style>
